const API_BASE = process.env.REACT_APP_BACKEND_API_URL;

type PresignResponse = {
  uploadUrl: string;
  objectKey: string;
  bucket: string;
};

type MeResponse = {
  avatarUrl?: string | null;
  avatarS3Key?: string | null;
};

function getAccessToken(): string | null {
  return localStorage.getItem("accessToken") || localStorage.getItem("idToken");
}

export async function fetchProfileMe(): Promise<MeResponse | null> {
  if (!API_BASE) return null;
  const token = getAccessToken();
  if (!token) return null;

  const res = await fetch(`${API_BASE}api/profile/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  return (await res.json()) as MeResponse;
}

export async function presignAvatarUpload(params: {
  contentType: string;
  extension: string;
}): Promise<PresignResponse> {
  if (!API_BASE) {
    throw new Error("API base URL is not configured");
  }
  const token = getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const res = await fetch(`${API_BASE}api/profile/avatar/presign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      contentType: params.contentType,
      extension: params.extension,
    }),
  });
  const data = (await res.json().catch(() => ({}))) as Partial<PresignResponse> & {
    detail?: string;
    message?: string;
  };
  if (!res.ok || !data.uploadUrl || !data.objectKey) {
    throw new Error(data.detail || data.message || "Failed to presign upload");
  }
  return data as PresignResponse;
}

export async function uploadToPresignedUrl(params: {
  uploadUrl: string;
  file: File;
}): Promise<void> {
  const res = await fetch(params.uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": params.file.type || "application/octet-stream",
    },
    body: params.file,
  });
  if (!res.ok) {
    throw new Error("Failed to upload file");
  }
}

export async function saveAvatarKey(params: { objectKey: string }): Promise<void> {
  if (!API_BASE) {
    throw new Error("API base URL is not configured");
  }
  const token = getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }

  const res = await fetch(`${API_BASE}api/profile/avatar`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ avatarS3Key: params.objectKey }),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { detail?: string; message?: string };
    throw new Error(data.detail || data.message || "Failed to save avatar");
  }
}

